import React, { Component } from 'react';
import FaqSection from './FaqSection';
import pEntitiesVideo from './assets/video/web/pentities-demo.mp4';
import msAppSource from './assets/MS_AppSource.webp';
import dm1 from './assets/docmgmt/dm1-knowledge-hub.webp';
import dm2 from './assets/docmgmt/dm2-search.webp';
import dm3 from './assets/docmgmt/dm3-versioning.webp';
import dm4 from './assets/docmgmt/dm4-record.webp';
import dm5 from './assets/docmgmt/dm5-retention.webp';
import wf1 from './assets/workflow/wf1-designer.webp';
import wf2 from './assets/workflow/wf2-step-check.webp';
import wf3 from './assets/workflow/wf3-run-history.webp';
import wf4 from './assets/workflow/wf4-blueprint.webp';
import wf5 from './assets/workflow/wf5-advisor.webp';
import './pentities.css';
import NavBar from './NavBar';

interface PEntitiesPageProps {}

interface PEntitiesPageState {
  isMenuOpen: boolean;
  isProductsMenuOpen: boolean;
  isSolutionsMenuOpen: boolean;
  activeModal: string | null;
  lightboxSrc: string | null;
  isVideoOpen: boolean;
  isPromoVideoOpen: boolean;
}

class PEntitiesPage extends Component<PEntitiesPageProps, PEntitiesPageState> {
  constructor(props: PEntitiesPageProps) {
    super(props);
    this.state = {
      isMenuOpen: false,
      isProductsMenuOpen: false,
      isSolutionsMenuOpen: false,
      activeModal: null,
      lightboxSrc: null,
      isVideoOpen: false,
      isPromoVideoOpen: false
    };
  }

  openPromoVideo = () => {
    this.setState({ isPromoVideoOpen: true });
  };

  closePromoVideo = () => {
    this.setState({ isPromoVideoOpen: false });
  };

  openLightbox = (src: string) => {
    this.setState({ lightboxSrc: src });
  };

  closeLightbox = () => {
    this.setState({ lightboxSrc: null });
  };

  openVideo = () => {
    this.setState({ isVideoOpen: true });
  };

  closeVideo = () => {
    this.setState({ isVideoOpen: false });
  };

  toggleMenu = () => {
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  };

  toggleProductsMenu = () => {
    this.setState({ isProductsMenuOpen: !this.state.isProductsMenuOpen });
  };

  closeProductsMenu = () => {
    this.setState({ isProductsMenuOpen: false });
  };

  toggleSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: !this.state.isSolutionsMenuOpen });
  };

  closeSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: false });
  };

  closeAllMenus = () => {
    this.setState({ isMenuOpen: false, isProductsMenuOpen: false, isSolutionsMenuOpen: false });
  };

  navigateHome = () => {
    window.location.href = '/';
  };

  scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      this.setState({ isMenuOpen: false, isProductsMenuOpen: false });
    }
  };

  openModal = (modalName: string) => {
    this.setState({ activeModal: modalName });
  };

  closeModal = () => {
    this.setState({ activeModal: null });
  };

  render() {
    return (
      <div className="pentities-page pentities-core-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="pentities-hero-section">
          <div className="pentities-hero-video-background">
            <video
              preload="metadata"
              autoPlay
              loop
              muted
              playsInline
              className="pentities-hero-video"
            >
              <source src={require('./assets/video/web/14311445_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="pentities-container">
            <div className="pentities-hero-content">
              <span className="pentities-hero-eyebrow">pEntities-Core</span>
              <h1 className="pentities-hero-title">
                AI-Powered Chatbot Platform for Microsoft 365 Automation
              </h1>
              <p className="pentities-hero-description">
                Transform your Microsoft 365 experience with intelligent document management, email automation, 
                SharePoint integration, and advanced AI capabilities. Automate tasks, analyze documents, and streamline 
                workflows with natural language commands.
              </p>
              <div className="pentities-hero-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pentities-hero-cta-btn"
                >
                  Request Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description Section */}
        <section id="overview" className="pentities-gendesc-section">
          <div className="pentities-container">
            <div className="pentities-gendesc-content">
              <span className="pentities-gendesc-label">General Description</span>
              <h2 className="pentities-gendesc-title">The Business Platform That Thinks With You</h2>
              <p>
                pEntities lives inside Microsoft Teams and turns every contract, case, employee or project into a
                living workspace documents, emails, tasks, people and AI in one place. Not another database. A
                digital partner for every team.
              </p>
              <p>
                <b>One complete picture.</b> Everything attached to a record documents, correspondence, tasks and
                figures gathers itself into a single view. Nothing lost, nothing scattered across inboxes and
                folders.
              </p>
              <p>
                <b>Documents that fill in the forms.</b> Invoices, receipts, identity documents and contracts are
                read the moment they arrive, and the data lands where it belongs in seconds rather than being
                retyped.
              </p>
              <p>
                <b>An expert for each department.</b> AI agents for legal, HR, finance and logistics that know your
                own documents and data, and can find, draft, analyse and cross-check. Not a generic chatbot a
                colleague who knows the business.
              </p>
              <p>
                <b>Nothing new to buy or learn.</b> It runs inside Microsoft Teams on the Microsoft 365 you already
                own, in your own tenant, with the sign-in your people already use and HR, legal, ticketing and
                finance all live on the same platform.
              </p>

              <div className="pentities-gendesc-video">
                <p className="pentities-gendesc-video-label">See the platform in action</p>
                <div
                  className="pentities-gendesc-video-wrapper"
                  onClick={this.openPromoVideo}
                  role="button"
                  tabIndex={0}
                  aria-label="Play pEntities video"
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.openPromoVideo(); }}
                >
                  <video
                    className="pentities-gendesc-video-thumb"
                    src={`${pEntitiesVideo}#t=0.001`}
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <div className="pentities-gendesc-play-overlay">
                    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" className="pentities-gendesc-play-icon">
                      <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.55)" />
                      <polygon points="24,16 52,32 24,48" fill="#ffffff" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Document Management & Email Management Section */}
        <section id="document-management" className="pentities-docmgmt-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">Document Management &amp; the Knowledge Hub</span>
            <h2 className="pentities-docmgmt-title">Intelligent, compliant documents without moving a single file out of Microsoft 365</h2>
            <p className="pentities-docmgmt-lead">
              No new repository and no migration. Your files stay exactly where they are pEntities adds
              organisation, search, compliance and protection on top, inside the same Teams window your people
              already use.
            </p>

            <div className="pentities-docmgmt-block">
              <h3>Every document about a record, in one place</h3>
              <figure className="pentities-docmgmt-figure is-float" onClick={() => this.openLightbox(dm1)}>
                <img loading="lazy" decoding="async" src={dm1} alt="Attaching documents to a record from SharePoint libraries" />
                <figcaption>Documents attached straight from SharePoint, with full version history and locking.</figcaption>
              </figure>
              <p>
                A brand, a project, an employee, a case each becomes one organised home that gathers the documents
                that matter, wherever they already sit. Nothing is copied or moved, so an edit made in Word or Excel
                is visible immediately and there is never a second version to reconcile.
              </p>
              <p>
                Everything keeps the version history, the categories and the fields your organisation already uses,
                and two people can work on the same file without overwriting each other.
              </p>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>Find it in seconds, read it without downloading it</h3>
              <figure className="pentities-docmgmt-figure is-float is-left" onClick={() => this.openLightbox(dm2)}>
                <img loading="lazy" decoding="async" src={dm2} alt="Tenant-wide document search with advanced filtering" />
                <figcaption>Search across everything a person is allowed to see, and open it in place.</figcaption>
              </figure>
              <p>
                One search covers every document in the organisation and returns only what that person is allowed to
                see, with a preview, who last changed it and where it lives. Searches a team runs often can be saved
                and repeated in a click.
              </p>
              <figure className="pentities-docmgmt-figure is-float" onClick={() => this.openLightbox(dm3)}>
                <img loading="lazy" decoding="async" src={dm3} alt="Working with native SharePoint metadata and taxonomy" />
                <figcaption>Your existing categories and fields, edited inside Teams no parallel system to maintain.</figcaption>
              </figure>
              <p>
                Word, Excel, PowerPoint and PDF open in full fidelity inside Teams, so people read in place and no
                loose copies leave the business.
              </p>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>Compliant by default, and provably so</h3>
              <figure className="pentities-docmgmt-figure is-float is-left" onClick={() => this.openLightbox(dm4)}>
                <img loading="lazy" decoding="async" src={dm4} alt="Document declared as a record under Microsoft Purview" />
                <figcaption>A document declared as a record, with its disposition date and alerts.</figcaption>
              </figure>
              <p>
                Retention rules, record declaration and expiry reminders are part of everyday handling rather than a
                separate exercise, using the same Microsoft governance that already protects the rest of your
                organisation. Every compliance event is logged with who, when and why.
              </p>
              <figure className="pentities-docmgmt-figure is-float" onClick={() => this.openLightbox(dm5)}>
                <img loading="lazy" decoding="async" src={dm5} alt="Applying Microsoft Purview retention labels" />
                <figcaption>Applying retention rules and setting expiry and notification dates.</figcaption>
              </figure>
              <p>
                When a document does have to leave, it is watermarked with who took it and when, access follows the
                permissions you already have in place, and the download is recorded. Everything stays in your own
                tenant, encrypted, and nothing leaves the EU.
              </p>
            </div>
          </div>
        </section>

        {/* Workflow Automation Section */}
        <section id="workflows" className="pentities-workflow-section">
          <div className="pentities-container">
            <span className="pentities-workflow-eyebrow">Workflow Automation &amp; the AI Workflow Agent</span>
            <h2 className="pentities-workflow-title">Describe the problem. Get a running process. Without leaving Microsoft 365.</h2>
            <p className="pentities-workflow-lead">
              The contract that should be reviewed before it renews. The joiner who needs six things to happen in
              order. The request that must reach the right team before its deadline. pEntities turns each one into a
              process that runs itself designed inside Teams, on your own data, in your own tenant.
            </p>

            <div className="pentities-workflow-block">
              <h3>Design it by drawing it</h3>
              <figure className="pentities-workflow-figure is-float" onClick={() => this.openLightbox(wf1)}>
                <img loading="lazy" decoding="async" src={wf1} alt="The workflow designer inside Microsoft Teams" />
                <figcaption>The designer inside Teams the steps on the canvas, and the panel that configures them.</figcaption>
              </figure>
              <p>
                Processes are built by dragging steps onto a canvas and connecting them, in the same Teams window
                the work already happens in. The result is a diagram anyone in the room can read.
              </p>
              <p>
                A process can start on a date, on a record or field changing, on a file arriving, on an email, or on
                a business event nearly forty in all and then do more than forty things in response: send and file
                correspondence, generate documents from templates, extract data from invoices and contracts, assign
                work, update records, or call one of your other systems.
              </p>
              <p>
                It also waits the way real processes wait. An approval can sit for days or weeks, asked by email
                with your own buttons Renew, Renegotiate, Let it lapse and the process continues from the answer.
                No app, no login.
              </p>
            </div>

            <div className="pentities-workflow-block">
              <h3>Checked before it goes live, accountable after</h3>
              <figure className="pentities-workflow-figure is-float is-left" onClick={() => this.openLightbox(wf2)}>
                <img loading="lazy" decoding="async" src={wf2} alt="Step Check validation" />
                <figcaption>Every step checked before publication, so problems surface at design time.</figcaption>
              </figure>
              <p>
                Before publication, pEntities checks the whole process and points out anything that would fail. You
                can then rehearse it on real data without affecting anything, and see exactly what each step would
                do and which path it would take.
              </p>
              <figure className="pentities-workflow-figure is-float" onClick={() => this.openLightbox(wf3)}>
                <img loading="lazy" decoding="async" src={wf3} alt="Run history" />
                <figcaption>Run history every execution, step by step, with the detail of each one a click away.</figcaption>
              </figure>
              <p>
                Once live, every run is listed step by step with its status, duration and any errors. Processes
                recover from interruptions on their own, safety limits stop a bad design running away, and
                everything workflows, history and logs stays inside your own tenant, running with the permissions
                of the person who owns it.
              </p>
            </div>

            <div className="pentities-workflow-block automation-blueprint-block">
              <span className="automation-blueprint-badge">Blueprint · the AI workflow agent</span>
              <h3>Describe the problem in a sentence. Get the workflows that fix it.</h3>
              <figure className="pentities-workflow-figure is-float" onClick={() => this.openLightbox(wf4)}>
                <img loading="lazy" decoding="async" src={wf4} alt="Blueprint problem to recommended workflows" />
                <figcaption>Blueprint the problem in your own words, the diagnosis, and the workflows it recommends.</figcaption>
              </figure>
              <p className="automation-blueprint-quote">
                "Sales misses renewal dates and Legal is brought in too late."
              </p>
              <p>
                That one sentence is the whole brief. Blueprint reads it the way an experienced consultant would
                who is involved, where the process breaks and what it is costing then prescribes concrete
                workflows and builds the ones you approve on the canvas, ready to review and put live. Nothing is
                left as a suggestion, and every recommendation is verified against what the platform can genuinely
                do before you see it.
              </p>
              <figure className="pentities-workflow-figure is-float is-left" onClick={() => this.openLightbox(wf5)}>
                <img loading="lazy" decoding="async" src={wf5} alt="The Workflow Advisor beside the designer" />
                <figcaption>The Workflow Advisor suggests workflows from what it sees; any design is one click from being created.</figcaption>
              </figure>
              <p>
                Day to day, the <b>Workflow Advisor</b> continues the same conversation beside the designer
                suggesting automations worth having, building the one you describe in a sentence, and answering
                questions as they come up.
              </p>
            </div>
          </div>
        </section>

        {/* pEntities Free the no-cost edition */}
        <section id="free" className="pentities-free-section">
          <div className="pentities-container">
            <div className="pentities-free-head">
              <span className="pentities-free-badge">Free edition · from Microsoft AppSource</span>
              <h2 className="pentities-free-title">pEntities Free</h2>
              <p className="pentities-free-lead">
                Organise everything you already own documents, tasks, mail and records inside Microsoft Teams,
                with no migration, no new infrastructure and no cloud bill. Installed by a Teams administrator in
                minutes, it uses only the Microsoft 365 licences you already pay for.
              </p>
            </div>

            <div className="pentities-free-pillars">
              <div className="pentities-free-pillar">
                <h3>Entity workspaces</h3>
                <p>
                  Every client, project, contract or case becomes one workspace that gathers its documents, emails,
                  tasks and people. The files never move they stay in SharePoint, Teams and OneDrive exactly where
                  they are.
                </p>
              </div>
              <div className="pentities-free-pillar">
                <h3>Document management</h3>
                <p>
                  Attach from anywhere, preview Word, Excel, PowerPoint and PDF in place, search the whole
                  organisation, generate documents from templates, and see which other records use the same file.
                </p>
              </div>
              <div className="pentities-free-pillar">
                <h3>Metadata &amp; versions</h3>
                <p>
                  Edit the real SharePoint fields from the record, compare any two versions side by side, check files
                  in and out safely, and manage sharing and permissions without leaving Teams.
                </p>
              </div>
              <div className="pentities-free-pillar">
                <h3>Tasks in Microsoft Planner</h3>
                <p>
                  Tasks raised from a record are real Planner tasks, so they appear in Planner, To Do and Teams
                  the same board your people already check every morning. Nothing is trapped inside pEntities.
                </p>
              </div>
              <div className="pentities-free-pillar">
                <h3>Rules that react</h3>
                <p>
                  Bind any field to a rule: when its value changes, raise a task, send an email or call a Power
                  Automate flow you already have. Configured by pointing and clicking, with no developer involved.
                </p>
              </div>
              <div className="pentities-free-pillar">
                <h3>Notes, retention &amp; email</h3>
                <p>
                  Meeting notes and call logs kept in OneNote against the record, emails and attachments filed with
                  it, and your existing Microsoft retention labels and record declaration applied in place.
                </p>
              </div>
            </div>

            <div className="pentities-free-split">
              <div className="pentities-free-col pentities-free-col-free">
                <h3>Free no Azure services</h3>
                <ul>
                  <li>Entity workspaces and content organisation</li>
                  <li>Document linking, preview, upload and search</li>
                  <li>SharePoint metadata, content types and taxonomy</li>
                  <li>Version history, check-out, sharing and permissions</li>
                  <li>Microsoft Planner tasks and checklists</li>
                  <li>Field rules tasks, email and Power Automate</li>
                  <li>Retention labels and record declaration</li>
                  <li>Outlook email and attachment filing</li>
                </ul>
              </div>
              <div className="pentities-free-col pentities-free-col-premium">
                <h3>Premium adds the intelligence</h3>
                <ul>
                  <li>AI knowledge base and document chat</li>
                  <li>Data extraction from invoices, contracts and IDs</li>
                  <li>Meaning-based search across the whole estate</li>
                  <li>Understanding of scanned documents and images</li>
                  <li>The visual workflow engine and Blueprint</li>
                  <li>Guest portal for external review and approval</li>
                  <li>HR, Legal and Finance modules</li>
                  <li>Electronic signature integration</li>
                </ul>
              </div>
            </div>

            <div className="pentities-free-close">
              <p>
                <b>Organisation is the problem most teams actually need solved.</b> Content that is findable,
                attached to the right record, correctly labelled and connected to a task list is content a team can
                work with. Use the free edition for as long as you like, then decide on evidence rather than on a
                promise whether the AI capabilities are worth adding. Nothing has to be rebuilt when you do.
              </p>
            </div>
          </div>
        </section>

        {/* Product Demo Video Section */}
        <section className="demo-video-section">
          <div className="pentities-container">
            <div className="pentities-demo-layout">
              <div className="pentities-demo-content">
                <span className="pentities-demo-eyebrow">See pEntities in Action</span>
                <h2 className="pentities-demo-title">
                  Watch our product presentation to see how pEntities revolutionizes Microsoft 365 automation
                </h2>
                <p>
                  In a few minutes you'll see how a contract, a case or an employee record becomes a living
                  workspace documents, emails, tasks and AI working together inside Microsoft Teams.
                </p>
                <p>
                  Watch documents read themselves with Document Intelligence, AI agents draft and analyse in
                  context, and no-code workflows route approvals and enforce deadlines on their own.
                </p>
                <p>
                  Everything shown is live in the product today, running entirely inside your own Microsoft 365
                  and Azure tenant no migration, no new logins, and no data leaving your control.
                </p>
              </div>
              <div className="pentities-demo-media">
                <div
                  className="pentities-demo-video-wrapper"
                  onClick={this.openVideo}
                  role="button"
                  tabIndex={0}
                  aria-label="Play pEntities product presentation"
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.openVideo(); }}
                >
                  <img loading="lazy" decoding="async"
                    className="pentities-demo-video-thumb"
                    src="https://img.youtube.com/vi/JrPHYmdWXkU/maxresdefault.jpg"
                    alt="pEntities Product Presentation"
                  />
                  <div className="pentities-demo-play-overlay">
                    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" className="pentities-demo-play-icon">
                      <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.55)" />
                      <polygon points="24,16 52,32 24,48" fill="#ffffff" />
                    </svg>
                  </div>
                </div>
                <p className="pentities-demo-video-caption">Click to watch the full presentation</p>
              </div>
            </div>
            <div className="download-section">
              <h3 className="download-title">Get pEntities Now</h3>
              <p className="download-description">
                Download pEntities directly from Microsoft AppSource and revolutionize your Microsoft 365 automation
              </p>
              <a 
                href="https://marketplace.microsoft.com/en-us/product/WA200009732" 
                target="_blank" 
                rel="noopener noreferrer"
                className="appsource-link"
              >
                <img loading="lazy" decoding="async" 
                  src={msAppSource} 
                  alt="Get it from Microsoft AppSource" 
                  className="appsource-badge"
                />
              </a>
            </div>
          </div>
        </section>

        {/* Modules Section */}
        <section id="modules" className="pentities-modules-section">
          <div className="pentities-container">
            <span className="pentities-modules-eyebrow">pEntities Modules</span>
            <h2 className="pentities-modules-title">Purpose-built modules for the work your teams actually do</h2>
            <p className="pentities-modules-lead">
              One platform, four specialised modules each with its own domain AI agent, all running inside
              Microsoft Teams on the data you already own.
            </p>

            <div className="pentities-modules-grid">
              <a href="/solutions/hr-template" className="pentities-module-card">
                <h3>HR Module</h3>
                <p>
                  The full employee lifecycle onboarding with automatic task lists, leave with multi-level
                  approval, performance reviews, training tracked to its expiry date and payroll analysis. The HR
                  agent spots compliance risks and writes the reports for you.
                </p>
                <span className="pentities-module-link">Explore HR →</span>
              </a>

              <a href="/solutions/case-management" className="pentities-module-card">
                <h3>Legal Module</h3>
                <p>
                  Every case tracked from pre-trial to appeal court, lawyers, opponents, confidentiality level,
                  financial exposure and risk. Contracts carry milestones, auto-renewal detection and signature
                  history, while the agent analyses timelines and exposure scenarios.
                </p>
                <span className="pentities-module-link">Explore Legal →</span>
              </a>

              <a href="/solutions/ticketing" className="pentities-module-card">
                <h3>Ticketing Module</h3>
                <p>
                  Every request handled with precision IT, HR, finance or legal. Real-time tracking, automatic
                  notifications, approval flows and full history, with priorities, SLAs and automation rules that
                  work without anyone intervening.
                </p>
                <span className="pentities-module-link">Explore Ticketing →</span>
              </a>

              <a href="/solutions/finance" className="pentities-module-card">
                <h3>Finance Module</h3>
                <p>
                  Invoices, expenses, purchases, budgets, receivables and tax data organised by fiscal year. Every
                  figure links back to its original email, document and approval task full traceability, and an
                  agent that spots trends and deviations.
                </p>
                <span className="pentities-module-link">Explore Finance →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Modal */}
        {this.state.activeModal && (
          <div className="pentities-modal-overlay" onClick={this.closeModal}>
            <div className="pentities-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="pentities-modal-close" onClick={this.closeModal}>&times;</button>
              <div className="pentities-modal-body">
                {/* Content will be added later */}
              </div>
            </div>
          </div>
        )}

        {/* Video Modal */}
        {this.state.isVideoOpen && (
          <div className="pentities-video-modal" onClick={this.closeVideo} role="dialog" aria-modal="true" aria-label="pEntities product presentation">
            <button className="pentities-video-close" onClick={this.closeVideo} aria-label="Close video">✕</button>
            <div className="pentities-video-frame" onClick={(e) => e.stopPropagation()}>
              <iframe
                src="https://www.youtube.com/embed/JrPHYmdWXkU?autoplay=1&rel=0"
                title="pEntities Product Presentation"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}

        {/* General Description Video Modal */}
        {this.state.isPromoVideoOpen && (
          <div className="pentities-video-modal" onClick={this.closePromoVideo} role="dialog" aria-modal="true" aria-label="pEntities video">
            <button className="pentities-video-close" onClick={this.closePromoVideo} aria-label="Close video">✕</button>
            <div className="pentities-video-frame" onClick={(e) => e.stopPropagation()}>
              <video
                src={pEntitiesVideo}
                controls
                autoPlay
                playsInline
              />
            </div>
          </div>
        )}

        {/* Image Lightbox */}
        {this.state.lightboxSrc && (
          <div className="pentities-lightbox" onClick={this.closeLightbox} role="dialog" aria-modal="true">
            <button className="pentities-lightbox-close" onClick={this.closeLightbox} aria-label="Close">✕</button>
            <img loading="lazy" decoding="async"
              className="pentities-lightbox-img"
              src={this.state.lightboxSrc}
              alt="Enlarged screenshot"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Footer */}
        <FaqSection path="/solutions/pentities" />
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>

                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>

                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default PEntitiesPage;

